import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Users, FileText, Clock, AlertTriangle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { DocumentStatus, DeadlineStatus } from '@taxistry/types';
import { useClients, useDocuments, useDeadlines } from '@/hooks/queries';

export function OverviewPage() {
  const { t } = useTranslation();

  const clients = useClients({ limit: 1 });
  const newDocs = useDocuments({ status: DocumentStatus.NEW, limit: 5 });
  const pendingDeadlines = useDeadlines({ status: DeadlineStatus.PENDING, limit: 5, sortBy: 'dueDate', sortOrder: 'asc' });
  const overdueDeadlines = useDeadlines({ status: DeadlineStatus.OVERDUE, limit: 5 });

  const stats = [
    {
      label: t('nav.clients'),
      value: clients.data?.total ?? '—',
      icon: Users,
      color: 'text-primary',
      to: '/clients' as const,
    },
    {
      label: t('documents.inbox'),
      value: newDocs.data?.total ?? '—',
      icon: FileText,
      color: 'text-blue-600',
      to: '/documents' as const,
    },
    {
      label: t('deadlines.upcoming'),
      value: pendingDeadlines.data?.total ?? '—',
      icon: Clock,
      color: 'text-amber-600',
      to: '/deadlines' as const,
    },
    {
      label: t('deadlines.overdue'),
      value: overdueDeadlines.data?.total ?? '—',
      icon: AlertTriangle,
      color: 'text-red-600',
      to: '/deadlines' as const,
    },
  ];

  const isLoading = clients.isLoading || newDocs.isLoading || pendingDeadlines.isLoading;

  return (
    <div className="p-6 space-y-6">
      <h1 className="font-display text-xl font-bold">{t('nav.overview')}</h1>

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Link key={stat.label} to={stat.to}>
            <Card className="border border-border rounded-[13px] bg-surface-2 hover:shadow-lifted-lg hover:-translate-y-0.5 transition-all cursor-pointer relative overflow-hidden group">
              <CardContent className="p-3">
                <p className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-bold mb-1">{stat.label}</p>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <p className="text-[26px] font-bold leading-tight">{stat.value}</p>
                )}
                <stat.icon className={`absolute right-4 bottom-3 h-10 w-10 ${stat.color} opacity-[0.05] group-hover:opacity-[0.10] transition-opacity`} />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent documents */}
        <Card>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-display text-sm font-bold">{t('documents.inbox')}</span>
            <Link to="/documents" className="text-xs text-primary hover:underline flex items-center gap-1">
              {t('common.all', 'Όλα')} <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <CardContent className="pt-4">
            {newDocs.isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !newDocs.data?.data.length ? (
              <p className="text-sm text-muted-foreground py-4">{t('documents.noDocuments')}</p>
            ) : (
              <div className="timeline">
                {newDocs.data.data.map((doc) => (
                  <div key={doc.id} className="timeline-item flex items-center justify-between py-2 hover:bg-muted/20 transition-colors rounded-sm px-1">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.originalFilename}</p>
                      <p className="text-xs text-muted-foreground">
                        {doc.clientBusinessName ?? '—'} &middot; {new Date(doc.createdAt).toLocaleDateString('el-GR')}
                      </p>
                    </div>
                    <Badge variant="secondary">{t(`documents.status.${doc.status}`)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming deadlines */}
        <Card>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-display text-sm font-bold">{t('deadlines.upcoming')}</span>
            <Link to="/deadlines" className="text-xs text-primary hover:underline flex items-center gap-1">
              {t('common.all', 'Όλες')} <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <CardContent className="pt-4">
            {pendingDeadlines.isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !pendingDeadlines.data?.data.length ? (
              <p className="text-sm text-muted-foreground py-4">{t('deadlines.noDeadlines')}</p>
            ) : (
              <div className="timeline">
                {pendingDeadlines.data.data.map((dl) => {
                  const due = new Date(dl.dueDate);
                  const today = new Date();
                  const daysLeft = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                  const urgency = daysLeft <= 3 ? 'destructive' : daysLeft <= 7 ? 'warning' : 'secondary';

                  return (
                    <div key={dl.id} className="timeline-item flex items-center justify-between py-2 hover:bg-muted/20 transition-colors rounded-sm px-1">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{dl.obligationNameEl ?? dl.periodLabel}</p>
                        <p className="text-xs text-muted-foreground">
                          {dl.clientBusinessName ?? '—'} &middot; {due.toLocaleDateString('el-GR')}
                        </p>
                      </div>
                      <Badge variant={urgency}>
                        {daysLeft > 0 ? `${daysLeft} ${t('deadlines.daysLeft')}` : t('deadlines.status.OVERDUE')}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
